// Disciplines.tsx
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';

interface DisciplineConfig {
	angle: number;
	text: string;
	x: number;
	y: number;
	anchor: string;
}

interface DisciplinesProps {
	disciplines: {
		showJumping: number;
		crossCountry: number;
		dressage: number;
		western: number;
		driving: number;
		racing: number;
	};
}

const Disciplines: React.FC<DisciplinesProps> = ({ disciplines }) => {
	const { t } = useTranslation();
	const cx = 150;
	const cy = 125;
	const [maxR, setMaxR] = useState(120);
	const [startR, setStartR] = useState(15);
	const [disciplinePoints, setDisciplinePoints] = useState('');
	const outerPolygonPoints: { [key: string]: { points: { x: number; y: number }; angle: number } } = {};
	const config: { [key: string]: DisciplineConfig } = {
		dressage: {
			angle: 30,
			text: t('horse.discipline.dressage'),
			x: 1,
			y: 5,
			anchor: 'start',
		},
		western: {
			angle: 90,
			text: t('horse.discipline.western'),
			x: 0,
			y: 15,
			anchor: 'middle',
		},
		racing: {
			angle: 150,
			text: t('horse.discipline.racing'),
			x: 1,
			y: 5,
			anchor: 'end',
		},
		driving: {
			angle: 210,
			text: t('horse.discipline.driving'),
			x: 1,
			y: 0,
			anchor: 'end',
		},
		showJumping: {
			angle: 270,
			text: t('horse.discipline.showJumping'),
			x: 0,
			y: -8,
			anchor: 'middle',
		},
		crossCountry: {
			angle: 330,
			text: t('horse.discipline.crossCountry'),
			x: 1,
			y: 0,
			anchor: 'start',
		},
	};

	useEffect(() => {
		const updateRadius = () => {
			if (window.innerWidth >= 1280) {
				setMaxR(120); // xl screen
				setStartR(25);
			} else if (window.innerWidth >= 1024) {
				setMaxR(100); // lg screen
				setStartR(20);
			} else {
				setMaxR(100); // default
				setStartR(20);
			}
		};

		updateRadius();
		window.addEventListener('resize', updateRadius);
		return () => window.removeEventListener('resize', updateRadius);
	}, []);

	useEffect(() => {
		calculateDisciplinePoints();
	}, [disciplines, maxR]);

	const calculateHexagons = () => {
		const polygonPoints = [];
		const r = startR;
		const startAngle = 30;
		const jumps = 60;

		for (let i = r; i < maxR; i += startR) {
			polygonPoints.push(generatePolygonPoints(cx, cy, i, startAngle, jumps));
		}
		return polygonPoints;
	};

	const generatePolygonPoints = (cx: number, cy: number, r: number, startAngle: number, jumps: number) => {
		const points = [];

		for (let i = startAngle; i < 360; i += jumps) {
			const x = cx + r * Math.cos((Math.PI / 180) * i);
			const y = cy + r * Math.sin((Math.PI / 180) * i);
			outerPolygonPoints[`${i}`] = { points: { x: Math.round(x), y: Math.round(y) }, angle: i };
			points.push(`${Math.round(x)},${Math.round(y)}`);
		}
		return points.join(' ');
	};

	const calculateDisciplinePoints = () => {
		const points = Object.entries(config).map(([key, { angle }]) => {
			const value = disciplines[key as keyof typeof disciplines];
			const normalizedValue = (value / 1000) * maxR;
			const x = cx + normalizedValue * Math.cos((Math.PI / 180) * angle);
			const y = cy + normalizedValue * Math.sin((Math.PI / 180) * angle);
			return `${Math.round(x)},${Math.round(y)}`;
		});
		setDisciplinePoints(points.join(' '));
	};

	return (
		<div id="svgBlock" className="flex text-white justify-center ml-7 md:ml-9 lg:ml-0 md:-mt-6 xl:mt-0">
			<svg className="relative z-9 h-[250px] w-[332px] overflow-visible p-1">
				{calculateHexagons().map((points: string, index: number) => (
					<polygon key={index} points={points} fill="none" stroke="white" strokeWidth="2" />
				))}
				<polygon points={disciplinePoints} fill="#fb923c" fillOpacity="0.3" stroke="#fb923c" strokeWidth="3" />
				{Object.keys(config).map((key) => (
					<text
						key={key}
						x={outerPolygonPoints[config[key].angle].points.x + config[key].x}
						y={outerPolygonPoints[config[key].angle].points.y + config[key].y}
						fontSize="14"
						textAnchor={config[key].anchor}
						fill="white"
						filter="url(#shadow)"
					>
						{config[key].text}
					</text>
				))}
			</svg>
		</div>
	);
};

export default Disciplines;
